import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'

import DateInput from '@components/inputs/textInput/dateInput'
import CheckBox from '@components/inputs/checkBox'
import SecondaryButton from '@components/inputs/button/secondaryButton'
import PrimaryButton from '@components/inputs/button/primaryButton'
import {
  checkInitialDateIsBefore,
  checkInitialDate
} from '@util/timeUtil'
import {
  CloseIcon,
  Area,
  CheckBoxArea,
  RowGap,
  Container,
  Section,
  LabelAndCheckBoxGroup,
  SearchContainer,
  AreaDateInput,
} from './styles'
import { AreaLabel, Title } from '../styles'

import 'material-icons/iconfont/outlined.css'

import Modal from '..'

const ComorbidityModal = ({
  filterState,
  setFilterState,
  isOpen,
  setClose,
}) => {
  const [temporaryState, setTemporaryState] = useState(filterState)
  const [dateErrors, setDateErrors] = useState({ startDate: false, endDate: false })

  const validateDatesIsBefore = () => {
    if (!temporaryState.startDate || !temporaryState.endDate) return

    if (!checkInitialDateIsBefore(temporaryState.startDate, temporaryState.endDate)) {
      setDateErrors((errors) => ({ ...errors, endDate: true }))
    } else {
      setDateErrors((errors) => ({ ...errors, endDate: false }))
    }
  }

  const checkInitialDateIsValid = () => {
    if (checkInitialDate(temporaryState.startDate)) {
      setDateErrors({ ...dateErrors, startDate: true })
    } else {
      setDateErrors({ ...dateErrors, startDate: false })

    }
    validateDatesIsBefore()

  }

  useEffect(() => {
    setTemporaryState(filterState)
  }, [filterState])

  const setField = (field, value) => {
    setTemporaryState({ ...temporaryState, [field]: value })
  }

  const onClean = () => {
    const resetState = {
      page: 0,
      noRisk: false,
      highRisk: false,
      mediumRisk: false,
      lowRisk: false,
      diabetic: false,
      hypertensive: false,
      overweight: false,
      dyslipidemic: false,
      todayCollections: false,
      lastMonthCollections: false,
      lastWeekCollections: false,
      lastSixMonthsCollections: false,
      male: false,
      female: false,
      collectionExpired: false,
      beneficiaryAge: null,
      search: null,
      startDate: '',
      endDate: ''
    }
    setFilterState(resetState)
    setTemporaryState(resetState)
  }

  const onApply = () => {
    const updatedState = { ...temporaryState }
    if (updatedState.beneficiaryAge === 0) {
      delete updatedState.beneficiaryAge
    }
    setFilterState(updatedState)
    setClose()
  }

  const isAnyFieldFilled = () => {
    const {
      startDate, endDate, todayCollections, lastWeekCollections, lastMonthCollections,
      lastSixMonthsCollections, diabetic, hypertensive, dyslipidemic, overweight,
      highRisk, mediumRisk, lowRisk, noRisk, male, female, collectionExpired, search, beneficiaryAge
    } = temporaryState

    const isValidDate = (date) => date && !checkInitialDate(date)

    return (
      isValidDate(startDate)
      || isValidDate(endDate)
      || todayCollections
      || lastWeekCollections
      || lastMonthCollections
      || lastSixMonthsCollections
      || diabetic
      || hypertensive
      || dyslipidemic
      || overweight
      || highRisk
      || mediumRisk
      || lowRisk
      || noRisk
      || male
      || female
      || collectionExpired
      || search
      || beneficiaryAge > 0
    )
  }

  return (
    <Modal isOpen={isOpen} style={{ height: '80%', width: '50%', padding: 24 }}>
      <Container>
        <CloseIcon className="material-icons-outlined" onClick={setClose}>close</CloseIcon>
        <Title>Filtrar pesquisa</Title>
        <Section>
          <RowGap>
            <LabelAndCheckBoxGroup>
              <Area>
                <AreaDateInput>
                  <AreaLabel>Data inicial</AreaLabel>
                  <DateInput
                    placeholder="--/--/----"
                    value={temporaryState.startDate}
                    onChange={(e) => setField('startDate', e)}
                    error={dateErrors.startDate}
                    messageError="Data inicial inválida por favor insira uma data a partir de 2020"
                    onBlur={checkInitialDateIsValid}
                  />
                </AreaDateInput>
                <AreaDateInput>
                  <AreaLabel>Data final</AreaLabel>
                  <DateInput
                    value={temporaryState.endDate}
                    onChange={(e) => setField('endDate', e)}
                    error={dateErrors.endDate}
                    messageError="A Data final deve ser maior que a data inicial"
                    onBlur={validateDatesIsBefore}
                  />
                </AreaDateInput>
              </Area>
            </LabelAndCheckBoxGroup>
          </RowGap>
        </Section>

        <Section>
          <LabelAndCheckBoxGroup>
            <AreaLabel>Coletas realizadas:</AreaLabel>
            <CheckBoxArea>
              <CheckBox
                checked={temporaryState.todayCollections}
                onChange={(e) => setField('todayCollections', e)}
                label="Hoje"
              />
              <CheckBox
                checked={temporaryState.lastWeekCollections}
                onChange={(e) => setField('lastWeekCollections', e)}
                label="Último semana"
              />
              <CheckBox
                checked={temporaryState.lastMonthCollections}
                onChange={(e) => setField('lastMonthCollections', e)}
                label="Último mês"
              />
              <CheckBox
                checked={temporaryState.lastSixMonthsCollections}
                onChange={(e) => setField('lastSixMonthsCollections', e)}
                label="Últimos 6 meses"
              />
            </CheckBoxArea>
          </LabelAndCheckBoxGroup>

          <LabelAndCheckBoxGroup>
            <AreaLabel>Comorbidades:</AreaLabel>
            <CheckBoxArea>
              <CheckBox
                checked={temporaryState.diabetic}
                onChange={(e) => setField('diabetic', e)}
                label="Diabetes"
              />
              <CheckBox
                checked={temporaryState.hypertensive}
                onChange={(e) => setField('hypertensive', e)}
                label="Hipertensão"
              />
              <CheckBox
                checked={temporaryState.dyslipidemic}
                onChange={(e) => setField('dyslipidemic', e)}
                label="Dislipidemia"
              />
              <CheckBox
                checked={temporaryState.overweight}
                onChange={(e) => setField('overweight', e)}
                label="Sobrepeso"
              />
            </CheckBoxArea>
          </LabelAndCheckBoxGroup>

          <LabelAndCheckBoxGroup>
            <AreaLabel>Classificação de risco:</AreaLabel>
            <CheckBoxArea>
              <CheckBox
                checked={temporaryState.highRisk}
                onChange={(e) => setField('highRisk', e)}
                label="Alto"
              />
              <CheckBox
                checked={temporaryState.mediumRisk}
                onChange={(e) => setField('mediumRisk', e)}
                label="Moderado"
              />
              <CheckBox
                checked={temporaryState.lowRisk}
                onChange={(e) => setField('lowRisk', e)}
                label="Baixo"
              />
              <CheckBox
                checked={temporaryState.noRisk}
                onChange={(e) => setField('noRisk', e)}
                label="Sem risco"
              />
            </CheckBoxArea>
          </LabelAndCheckBoxGroup>
        </Section>

        <Section>
          <LabelAndCheckBoxGroup>
            <AreaLabel>Sexo do(a) beneficiário:</AreaLabel>
            <CheckBoxArea>
              <CheckBox
                checked={temporaryState.male}
                onChange={(e) => setField('male', e)}
                label="Homem"
              />
              <CheckBox
                checked={temporaryState.female}
                onChange={(e) => setField('female', e)}
                label="Mulher"
              />
            </CheckBoxArea>
          </LabelAndCheckBoxGroup>
        </Section>

        <Section>
          <LabelAndCheckBoxGroup>
            <AreaLabel>Status da coleta:</AreaLabel>
            <CheckBoxArea>
              <CheckBox
                checked={temporaryState.collectionExpired}
                onChange={(e) => setField('collectionExpired', e)}
                label="Coleta expirada (realizada somente no ano anterior)."
              />
            </CheckBoxArea>
          </LabelAndCheckBoxGroup>
        </Section>

        <Section>
          <LabelAndCheckBoxGroup>
            <AreaLabel>
              Idade do(a) beneficiário(a):
              {temporaryState.beneficiaryAge > 0 ? temporaryState.beneficiaryAge : '--'}
              anos
            </AreaLabel>
            <SearchContainer style={{ padding: '5px 0' }}>
              <input
                type="range"
                min="0"
                max="120"
                value={temporaryState.beneficiaryAge || 0}
                onChange={(e) => setField('beneficiaryAge', parseInt(e.target.value, 10))}
                style={{ width: '100%', cursor: 'pointer' }}
              />
            </SearchContainer>
          </LabelAndCheckBoxGroup>
        </Section>

        <RowGap>
          <SecondaryButton onClick={onClean}>Limpar</SecondaryButton>
          <PrimaryButton onClick={onApply} disabled={!isAnyFieldFilled()}>
            Aplicar
          </PrimaryButton>
        </RowGap>
      </Container>
    </Modal>
  )
}

ComorbidityModal.propTypes = {
  isOpen: PropTypes.bool,
  setClose: PropTypes.func.isRequired,
  filterState: PropTypes.shape({
    noRisk: PropTypes.bool,
    highRisk: PropTypes.bool,
    mediumRisk: PropTypes.bool,
    lowRisk: PropTypes.bool,
    diabetic: PropTypes.bool,
    hypertensive: PropTypes.bool,
    overweight: PropTypes.bool,
    dyslipidemic: PropTypes.bool,
    search: PropTypes.string,
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    todayCollections: PropTypes.bool,
    lastMonthCollections: PropTypes.bool,
    lastWeekCollections: PropTypes.bool,
    lastSixMonthsCollections: PropTypes.bool,
    male: PropTypes.bool,
    female: PropTypes.bool,
    collectionExpired: PropTypes.bool,
    beneficiaryAge: PropTypes.number,
  }),
  setFilterState: PropTypes.func,
}

ComorbidityModal.defaultProps = {
  isOpen: false,
  filterState: {
    noRisk: false,
    highRisk: false,
    mediumRisk: false,
    lowRisk: false,
    diabetic: false,
    hypertensive: false,
    overweight: false,
    dyslipidemic: false,
    todayCollections: false,
    lastMonthCollections: false,
    lastWeekCollections: false,
    lastSixMonthsCollections: false,
    male: false,
    female: false,
    collectionExpired: false,
    beneficiaryAge: 0,
    search: '',
  },
  setFilterState: () => { },
}

export default ComorbidityModal
